import React, { useRef } from 'react';
import { getComponentConfig } from '../../../config/componentTypes';
import CommonAttributesForm from './CommonAttributesForm';
import ComponentActionsForm from './ComponentActionsForm'; // Import Actions form for component configuration
import ComponentValidationsForm from './ComponentValidationsForm'; // Import Validations form for component configuration
import * as SpecificForms from './SpecificForms';

const Accordion = ({ 
  onSaveToLibrary,
  component, 
  updateComponent, 
  deleteComponent, 
  duplicateComponent,
  isExpanded, 
  onToggle,
  setRef,
  layoutId,
  allComponents = [],

  // fixed/updated by ChatGPT on 2026-01-25 17:05:00 – receive add handlers for GridForm per-cell creation
  onAddComponent,
  onAddFormComponent,
  onAddCardComponent,
  onAddFromLibrary
}) => {
  const accordionRef = useRef(null);
  const config = getComponentConfig(component.type);
  const SpecificForm = SpecificForms[config?.formComponent];

  React.useEffect(() => {
    if (accordionRef.current) {
      setRef(accordionRef.current);
    }
  }, [setRef]);

  return (
    <div ref={accordionRef} style={{
      border: '1px solid #E5E7EB',
      borderRadius: '8px',
      backgroundColor: '#FFFFFF',
      overflow: 'hidden'
    }}>
      <div
        onClick={onToggle}
        style={{
          padding: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          backgroundColor: isExpanded ? '#F3F4F6' : '#FFFFFF',
          borderBottom: isExpanded ? '1px solid #E5E7EB' : 'none'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
            {config?.label || component.type}
          </span>
          <span style={{ 
            fontSize: '11px', 
            color: '#6B7280',
            backgroundColor: '#F3F4F6',
            padding: '2px 6px',
            borderRadius: '4px'
          }}>
            {component.type}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onSaveToLibrary?.(component);
            }}
            style={{
              padding: "4px 8px",
              fontSize: "12px",
              backgroundColor: "transparent",
              border: "1px solid #10B981",
              borderRadius: "4px",
              cursor: "pointer",
              color: "#059669"
            }}
            title="Save to Library"
          >
            💾
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              duplicateComponent(component.id);
            }}
            style={{
              padding: '4px 8px',
              fontSize: '12px',
              backgroundColor: 'transparent',
              border: '1px solid #D1D5DB',
              borderRadius: '4px',
              cursor: 'pointer',
              color: '#374151'
            }}
            title="Duplicate"
          >
            📋
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (window.confirm('Delete this component?')) {
                deleteComponent(component.id);
              }
            }}
            style={{
              padding: '4px 8px',
              fontSize: '12px',
              backgroundColor: 'transparent',
              border: '1px solid #FCA5A5',
              borderRadius: '4px',
              cursor: 'pointer',
              color: '#DC2626'
            }}
            title="Delete"
          >
            🗑️
          </button>
          <span style={{ fontSize: '12px', color: '#9CA3AF', marginLeft: '4px' }}>
            {isExpanded ? '▲' : '▼'}
          </span>
        </div>
      </div>

      {isExpanded && (
        <div style={{ padding: '16px' }}>
          {/* Section 1: Common Attributes (width, height, padding, etc.) */}
          <h4 style={{ fontSize: '13px', fontWeight: '600', marginBottom: '12px', color: '#374151' }}>
            Common Attributes
          </h4>
          <CommonAttributesForm component={component} onUpdate={updateComponent} />

          {/* Section 2: Component-Specific Attributes (varies by component type) */}
          {SpecificForm && (
            <>
              <h4 style={{ 
                fontSize: '13px', 
                fontWeight: '600', 
                marginTop: '16px', 
                marginBottom: '12px', 
                color: '#374151',
                paddingTop: '16px',
                borderTop: '1px solid #E5E7EB'
              }}>
                {config?.label} Attributes
              </h4>

              {/* <SpecificForm component={component} onUpdate={updateComponent} layoutId={layoutId} allComponents={allComponents} /> */}
              {/* fixed/updated by ChatGPT on 2026-01-25 17:05:00 – forward add handlers so GridForm can use ComponentSelector per cell */}
              <SpecificForm
                component={component}
                onUpdate={updateComponent}
                layoutId={layoutId}
                allComponents={allComponents}
                onAddComponent={onAddComponent}
                onAddFormComponent={onAddFormComponent}
                onAddCardComponent={onAddCardComponent}
                onAddFromLibrary={onAddFromLibrary}
                onDeleteComponent={deleteComponent}
              />
            </>
          )}

          {/* Section 3: Actions Configuration (dropdown to add actions to component) */}
          <h4 style={{ 
            fontSize: '13px', 
            fontWeight: '600', 
            marginTop: '16px', 
            marginBottom: '12px', 
            color: '#374151',
            paddingTop: '16px',
            borderTop: '1px solid #E5E7EB'
          }}>
            ⚡ Actions
          </h4>
          <ComponentActionsForm component={component} onUpdate={updateComponent} />
          <div style={{ 
            fontSize: '11px', 
            color: '#9CA3AF', 
            marginTop: '8px',
            padding: '8px',
            background: '#F9FAFB',
            borderRadius: '6px',
            border: '1px solid #E5E7EB'
          }}>
            💡 <strong>Tip:</strong> Actions trigger when users interact with this component (click, hover, etc.)
          </div>

          {/* Section 4: Validations Configuration (add visibility/conditional rules) */}
          {component.type !== 'form' && (
            <>
              <h4 style={{ 
                fontSize: '13px', 
                fontWeight: '600', 
                marginTop: '16px', 
                marginBottom: '12px', 
                color: '#374151',
                paddingTop: '16px',
                borderTop: '1px solid #E5E7EB'
              }}>
                ✅ Validations
              </h4>
              <ComponentValidationsForm component={component} onUpdate={updateComponent} />
              <div style={{ 
                fontSize: '11px', 
                color: '#9CA3AF', 
                marginTop: '8px',
                padding: '8px',
                background: '#F9FAFB',
                borderRadius: '6px',
                border: '1px solid #E5E7EB'
              }}>
                💡 <strong>Tip:</strong> Validations control when this component is visible (date, device, browser, etc.)
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default Accordion;
